/**
 * The Recorded wall's camera list: this node's cameras by group, each one draggable onto any
 * window. A click adds the camera to the first empty window — the same result without a mouse,
 * which is also what a touch screen gets (HTML5 drag does not fire on touch).
 *
 * Purely presentational, like the live wall's picker: the host passes the cameras and the flat
 * group list it already fetched. Camera shape is the `/cameras` one both nodes return —
 * `{ id, name, groups: [{ id }] }`.
 */
import { useMemo, useState, type DragEvent } from 'react';
import {
  MagnifyingGlassIcon, ChevronRightIcon, ChevronDownIcon, ChevronLeftIcon, FolderIcon, FolderOpenIcon, VideoCameraIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import { DND_CAMERA } from './PlaybackWallTile';
import { Tip } from '../common/Tooltip';

interface PanelCamera { id: string; name: string; groups?: { id: string }[]; [k: string]: unknown }
interface PanelGroup { id: string; name: string; parent_id?: string | null }

export interface PlaybackCameraPanelProps {
  cameras: PanelCamera[];
  groups: PanelGroup[];
  loading?: boolean;
  /** Cameras already on the wall — still draggable (to move them), marked as placed. */
  onWall: Set<string>;
  onAdd: (camera: { id: string; name: string }) => void;
  /**
   * Folded to a slim rail, like the app's side nav — the wall gets the width back once the
   * cameras are placed. Controlled by the host so it can remember the choice.
   */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  className?: string;
}

interface TreeNode { group: PanelGroup; children: TreeNode[]; cameras: PanelCamera[] }

export default function PlaybackCameraPanel({
  cameras, groups, loading, onWall, onAdd, collapsed = false, onToggleCollapsed, className = '',
}: PlaybackCameraPanelProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  const { roots, ungrouped } = useMemo(() => {
    const nodes = new Map<string, TreeNode>();
    for (const g of groups) nodes.set(String(g.id), { group: g, children: [], cameras: [] });
    const roots: TreeNode[] = [];
    for (const n of nodes.values()) {
      const parent = n.group.parent_id ? nodes.get(String(n.group.parent_id)) : null;
      if (parent) parent.children.push(n); else roots.push(n);
    }
    const ungrouped: PanelCamera[] = [];
    for (const c of cameras) {
      const gs = (c.groups || []).map((g) => nodes.get(String(g.id))).filter(Boolean) as TreeNode[];
      if (gs.length) gs.forEach((g) => g.cameras.push(c)); else ungrouped.push(c);
    }
    const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, undefined, { numeric: true });
    const sortTree = (list: TreeNode[]) => {
      list.sort((a, b) => byName(a.group, b.group));
      for (const n of list) { n.cameras.sort(byName); sortTree(n.children); }
    };
    sortTree(roots);
    ungrouped.sort(byName);
    return { roots, ungrouped };
  }, [cameras, groups]);

  const q = search.trim().toLowerCase();
  const matches = useMemo(
    () => (q ? cameras.filter((c) => c.name.toLowerCase().includes(q)).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })) : []),
    [cameras, q],
  );

  const toggle = (id: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const countIn = (n: TreeNode): number => n.cameras.length + n.children.reduce((s, c) => s + countIn(c), 0);

  const renderCamera = (c: PanelCamera, depth: number) => {
    const placed = onWall.has(String(c.id));
    const onDragStart = (e: DragEvent) => {
      e.dataTransfer.setData(DND_CAMERA, JSON.stringify({ id: c.id, name: c.name }));
      e.dataTransfer.setData('text/plain', c.name);
      e.dataTransfer.effectAllowed = 'copy';
    };
    return (
      <Tip key={`c-${c.id}-${depth}`} side="right" className="w-full" label={placed ? `${c.name} — already on the wall` : `${c.name} — drag onto a window, or click to add`}>
      <button
        draggable
        onDragStart={onDragStart}
        onClick={() => onAdd({ id: String(c.id), name: c.name })}
        style={{ paddingLeft: 8 + depth * 14 }}
        className={`flex w-full cursor-grab items-center gap-2 rounded-md py-1.5 pr-2 text-left text-[12px] transition-colors hover:bg-muted active:cursor-grabbing ${
          placed ? 'text-brand' : 'text-foreground'
        }`}
      >
        <VideoCameraIcon className="h-3.5 w-3.5 flex-none" />
        <span className="min-w-0 flex-1 truncate">{c.name}</span>
        {placed && <CheckIcon className="h-3.5 w-3.5 flex-none" />}
      </button>
      </Tip>
    );
  };

  const renderNode = (n: TreeNode, depth: number): React.ReactNode => {
    const id = String(n.group.id);
    const isOpen = open.has(id);
    const count = countIn(n);
    if (!count) return null;
    return (
      <div key={`g-${id}`}>
        <button
          onClick={() => toggle(id)}
          style={{ paddingLeft: 4 + depth * 14 }}
          className="flex w-full items-center gap-1.5 rounded-md py-1.5 pr-2 text-left text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
        >
          {isOpen ? <ChevronDownIcon className="h-3 w-3 flex-none text-muted-foreground" /> : <ChevronRightIcon className="h-3 w-3 flex-none text-muted-foreground" />}
          {isOpen ? <FolderOpenIcon className="h-4 w-4 flex-none text-brand" /> : <FolderIcon className="h-4 w-4 flex-none text-brand" />}
          <span className="min-w-0 flex-1 truncate">{n.group.name}</span>
          <span className="flex-none text-[10px] text-muted-foreground">{count}</span>
        </button>
        {isOpen && (
          <>
            {n.children.map((c) => renderNode(c, depth + 1))}
            {n.cameras.map((c) => renderCamera(c, depth + 1))}
          </>
        )}
      </div>
    );
  };

  // The width animates on the outer box; the round toggle sits on its edge, OUTSIDE the card's
  // overflow-hidden, exactly where the side nav keeps its own.
  return (
    <div
      className={`relative flex-none transition-[width] duration-300 ease-in-out ${collapsed ? 'w-11' : 'w-60 xl:w-64'} ${className}`}
    >
      {onToggleCollapsed && (
        // The wrapper carries the absolute position: an in-flow tooltip wrapper would add a line
        // box above the card, pushing the full-height card down onto the timeline below.
        <Tip side="right" label={collapsed ? 'Show the camera list' : 'Hide the camera list'} className="absolute -right-3.5 top-3 z-20">
        <button
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Show camera list' : 'Hide camera list'}
          aria-expanded={!collapsed}
          className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-border bg-card text-foreground shadow-md transition-colors hover:border-brand hover:text-brand"
        >
          {collapsed ? <ChevronRightIcon className="h-3.5 w-3.5" /> : <ChevronLeftIcon className="h-3.5 w-3.5" />}
        </button>
        </Tip>
      )}

      {collapsed ? (
        /* The rail: still says what it holds and how many are placed, and a click anywhere on it
           opens it — a 44px strip is too narrow to make the edge button the only way back. */
        <Tip side="right" label={`Show the camera list (${onWall.size} on the wall)`} className="h-full w-full">
        <button
          onClick={onToggleCollapsed}
          className="flex h-full w-full flex-col items-center gap-3 overflow-hidden rounded-lg border border-border bg-card pt-12 text-muted-foreground transition-colors hover:text-foreground"
        >
          <VideoCameraIcon className="h-5 w-5 text-brand" />
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-foreground">{onWall.size}</span>
          <span className="text-[11px] font-medium tracking-wide [writing-mode:vertical-rl]">Cameras</span>
        </button>
        </Tip>
      ) : (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border p-2 pr-4">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cameras…"
            className="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-[12px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          />
        </div>
        <p className="mt-1.5 px-0.5 text-[10px] text-muted-foreground">Drag a camera onto a window · click to add</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5" style={{ scrollbarWidth: 'thin' }}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-[12px] text-muted-foreground">
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted border-t-brand" /> Loading cameras…
          </div>
        ) : q ? (
          matches.length ? matches.map((c) => renderCamera(c, 0)) : <p className="py-6 text-center text-[12px] text-muted-foreground">No camera matches “{search}”</p>
        ) : cameras.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-muted-foreground">No cameras</p>
        ) : (
          <>
            {roots.map((n) => renderNode(n, 0))}
            {ungrouped.length > 0 && (
              <div>
                {roots.length > 0 && <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Ungrouped</p>}
                {ungrouped.map((c) => renderCamera(c, 0))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
      )}
    </div>
  );
}
